import React, { useState, useEffect } from 'react';
import { formatDateForInput } from '../../utils/dateUtils';
import { useUserContext } from '../../store/user_context';

const PeriodOrders = () => {
    const { user } = useUserContext();
    const [loading, setLoading] = useState(false);
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const [groupingBy, setGroupingBy] = useState(0);
    const [ordersData, setOrdersData] = useState(null);
    const [validationErrors, setValidationErrors] = useState({});

    const apiUrl = import.meta.env.VITE_API_URL;
    const ordersTotalForAccountantEndpoint = import.meta.env.VITE_get_orders_total_for_accountant;

    useEffect(() => {
        // Set default dates to today
        const today = formatDateForInput(new Date());
        setFromDate(today);
        setToDate(today);
    }, []);

    const fetchOrdersData = async (customGrouping = null) => {
        if (!fromDate || !toDate) return;

        try {
            setLoading(true);
            setValidationErrors({});
            
            const params = new URLSearchParams();
            params.append('fromDate', fromDate);
            params.append('endDate', toDate);
            params.append('by', (customGrouping !== null ? customGrouping : groupingBy).toString());

            const response = await fetch(`${apiUrl}${ordersTotalForAccountantEndpoint}?${params}`);

            if (response.ok) {
                const data = await response.json();
                setOrdersData(data);
            } else {
                console.error('Error fetching orders data');
                setValidationErrors({ submit: 'حدث خطأ أثناء جلب البيانات' });
            }
        } catch (error) {
            console.error('Error fetching orders data:', error);
            setValidationErrors({ submit: 'حدث خطأ أثناء جلب البيانات' });
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = () => {
        // Clear previous validation errors
        setValidationErrors({});
        
        // Validate dates
        if (!fromDate) {
            setValidationErrors(prev => ({ ...prev, fromDate: 'يرجى اختيار تاريخ البداية' }));
            return;
        }
        if (!toDate) {
            setValidationErrors(prev => ({ ...prev, toDate: 'يرجى اختيار تاريخ النهاية' }));
            return;
        }
        if (new Date(fromDate) > new Date(toDate)) {
            setValidationErrors(prev => ({ ...prev, toDate: 'تاريخ النهاية يجب أن يكون بعد تاريخ البداية' }));
            return;
        }
        
        fetchOrdersData();
    };

    const handleGroupingChange = (e) => {
        const newGrouping = parseInt(e.target.value);
        setGroupingBy(newGrouping);
        
        // Auto-fetch data when grouping changes if we have valid dates
        if (fromDate && toDate && !loading) {
            fetchOrdersData(newGrouping);
        }
    };

    const downloadExcel = () => {
        if (!ordersData) return;
        
        // Create CSV content
        let csvContent = '\uFEFF'; // UTF-8 BOM for Arabic
        csvContent += 'التاريخ,من,إلى,التجميع,الإجمالي,خدمة التوصيل,الإجمالي الكامل\n';
        
        const total = calculateTotal();
        const deliveryPrice = ordersData.deliveryPrice || 0;
        const grandTotal = total + deliveryPrice;
        
        csvContent += `"${fromDate}","${fromDate}","${toDate}","${getGroupingName()}","${total}","${deliveryPrice}","${grandTotal}"\n`;
        
        // Add detailed data based on grouping
        if (groupingBy === 0 && ordersData.employeeData) {
            csvContent += '\nالموظف,رقم الموظف,الوظيفة,إجمالي الطلبات\n';
            ordersData.employeeData.forEach((emp, index) => {
                csvContent += `"${emp.name}","${emp.nagwaId}","${emp.title || ''}","${emp.total}"\n`;
            });
        } else if (groupingBy === 1 && ordersData.menuData) {
            csvContent += '\nالمطعم,إجمالي الطلبات\n';
            ordersData.menuData.forEach((menu, index) => {
                csvContent += `"${menu.menuName}","${menu.total}"\n`;
            });
        } else if (groupingBy === 2 && ordersData.buildingData) {
            csvContent += '\nالمبنى,إجمالي الطلبات\n';
            ordersData.buildingData.forEach((building, index) => {
                csvContent += `"${building.buildingName}","${building.total}"\n`;
            });
        }

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `تقرير_الطلبات_${fromDate}_${toDate}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const calculateTotal = () => {
        if (!ordersData) return 0;
        
        switch (groupingBy) {
            case 0:
                return ordersData.employeeData?.reduce((sum, emp) => sum + emp.total, 0) || 0;
            case 1:
                return ordersData.menuData?.reduce((sum, menu) => sum + menu.total, 0) || 0;
            case 2:
                return ordersData.buildingData?.reduce((sum, building) => sum + building.total, 0) || 0;
            default:
                return 0;
        }
    };

    const getGroupingName = () => {
        switch (groupingBy) {
            case 0: return 'الموظف';
            case 1: return 'المطعم';
            case 2: return 'المبنى';
            default: return 'الموظف';
        }
    };

    const total = calculateTotal();
    const deliveryPrice = ordersData?.deliveryPrice || 0;
    const grandTotal = total + deliveryPrice;

    return (
        <section className="main_content">
            <style>
                {`
                    .period-orders-table td {
                        text-align: right !important;
                    }
                    .period-orders-table th {
                        text-align: right !important;
                    }
                    .period-orders-table bdo {
                        text-align: right !important;
                    }
                    input[type="date"] {
                        text-align: right !important;
                        direction: rtl !important;
                    }
                    input[type="date"]:hover {
                        border-color: #f6a500 !important;
                        box-shadow: 0 0 0 0.2rem rgba(246, 165, 0, 0.25) !important;
                    }
                    input[type="date"]:focus {
                        border-color: #f6a500 !important;
                        box-shadow: 0 0 0 0.2rem rgba(246, 165, 0, 0.25) !important;
                    }
                `}
            </style>
            <div className="container">
                <h1 className="page-title">إجمالي الطلبات خلال فترة</h1>

                {/* Export Button */}
                {ordersData && (total > 0 || deliveryPrice > 0) && (
                    <div className="sheet_actions">
                        <button 
                            type="button" 
                            className="btn_icon" 
                            onClick={downloadExcel}
                            title="تنزيل الملف"
                        >
                            <i className="mIcon">&#xf220;</i>
                        </button>
                    </div>
                )}

                {/* Form */}
                <form className="form-horizontal" onSubmit={(e) => { e.preventDefault(); handleSearch(); }}>
                    <div className="form-group">
                        <label>من:</label>
                        <div className="field">
                            <input
                                type="date"
                                value={fromDate}
                                onChange={(e) => {
                                    setFromDate(e.target.value);
                                    if (validationErrors.fromDate) {
                                        setValidationErrors(prev => ({ ...prev, fromDate: '' }));
                                    }
                                }}
                                className={`form-control ${validationErrors.fromDate ? 'error' : ''}`}
                            />
                            {validationErrors.fromDate && (
                                <div className="alert danger">
                                    {validationErrors.fromDate}
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="form-group">
                        <label>إلى:</label>
                        <div className="field">
                            <input
                                type="date"
                                value={toDate}
                                onChange={(e) => {
                                    setToDate(e.target.value);
                                    if (validationErrors.toDate) {
                                        setValidationErrors(prev => ({ ...prev, toDate: '' }));
                                    }
                                }}
                                className={`form-control ${validationErrors.toDate ? 'error' : ''}`}
                            />
                            {validationErrors.toDate && (
                                <div className="alert danger">
                                    {validationErrors.toDate}
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="form-group">
                        <label>التجميع طبقًا:</label>
                        <div className="field">
                            <select
                                value={groupingBy}
                                onChange={handleGroupingChange}
                                className="form-control"
                            >
                                <option value={0}>الموظف</option>
                                <option value={1}>المطعم</option>
                                <option value={2}>المبنى</option>
                            </select>
                        </div>
                    </div>

                    <button type="submit" className="btn btn-primary">
                        عرض
                    </button>
                </form>

                {/* Error Message */}
                {validationErrors.submit && (
                    <div className="alert danger">
                        {validationErrors.submit}
                    </div>
                )}

                {/* Loading */}
                {loading && (
                    <div className="loading-message">جاري التحميل...</div>
                )}

                {/* Results */}
                {ordersData && (total > 0 || deliveryPrice > 0) && (
                    <div className="food_list">
                        {/* Summary Table */}
                        <table className="table table-bordered table-curved period-orders-table">
                            <thead className="thead-inverse">
                                <tr>
                                    <th>الإجمالـــي بدون خدمة التوصيل</th>
                                    <th>خدمة التوصيل</th>
                                    <th>الإجمالي</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr className="odd">
                                    <td>
                                        <bdo dir="ltr">{total}</bdo>
                                    </td>
                                    <td>
                                        <bdo dir="ltr">{deliveryPrice}</bdo>
                                    </td>
                                    <td>
                                        <bdo dir="ltr">{grandTotal}</bdo>
                                    </td>
                                </tr>
                            </tbody>
                        </table>

                        {/* Employee Data */}
                        {groupingBy === 0 && ordersData.employeeData && ordersData.employeeData.length > 0 && (
                            <table className="table table-bordered table-curved period-orders-table">
                                <thead className="thead-inverse">
                                    <tr>
                                        <th>المسلسل</th>
                                        <th>رقم الموظف</th>
                                        <th>إسم الموظف</th>
                                        <th>الوظيفه</th>
                                        <th>إجمالى الطلبات</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {ordersData.employeeData.map((emp, index) => (
                                        <tr key={index}>
                                            <td>
                                                <bdo dir="ltr">{index + 1}</bdo>
                                            </td>
                                            <td>{emp.nagwaId}</td>
                                            <td>{emp.name}</td>
                                            <td>{emp.title || ''}</td>
                                            <td>
                                                <bdo dir="ltr">{emp.total}</bdo>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}

                        {/* Menu Data */}
                        {groupingBy === 1 && ordersData.menuData && ordersData.menuData.length > 0 && (
                            <table className="table table-bordered table-curved period-orders-table">
                                <thead className="thead-inverse">
                                    <tr>
                                        <th>المسلسل</th>
                                        <th>إسم المطعم</th>
                                        <th>إجمالى الطلبات</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {ordersData.menuData.map((menu, index) => (
                                        <tr key={index}>
                                            <td>
                                                <bdo dir="ltr">{index + 1}</bdo>
                                            </td>
                                            <td>{menu.menuName}</td>
                                            <td>
                                                <bdo dir="ltr">{menu.total}</bdo>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}

                        {/* Building Data */}
                        {groupingBy === 2 && ordersData.buildingData && ordersData.buildingData.length > 0 && (
                            <table className="table table-bordered table-curved period-orders-table">
                                <thead className="thead-inverse">
                                    <tr>
                                        <th>المسلسل</th>
                                        <th>إجمالي الطلبات</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {ordersData.buildingData.map((building, index) => (
                                        <tr key={index}>
                                            <td>
                                                <bdo dir="ltr">{index + 1}</bdo>
                                            </td>
                                            <td>
                                                <bdo dir="ltr">{building.total}</bdo>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                )}

                {/* No Data Message */}
                {ordersData && total === 0 && deliveryPrice === 0 && (
                    <div className="no-data">لا توجد بيانات للعرض</div>
                )}
            </div>
        </section>
    );
};

export default PeriodOrders;
